import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaUserFriends, FaChevronDown, FaPhone } from "react-icons/fa";

const PANEL_WIDTH = 256;

const ContactLine = ({ contact }) => (
  <div className="flex flex-col min-w-0">
    <span className="text-xs font-semibold text-gray-800 truncate" title={contact.name}>
      {contact.name}
    </span>
    {contact.tel && (
      <a
        href={`tel:${contact.tel}`}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-indigo-600 truncate w-fit"
      >
        <FaPhone className="w-2.5 h-2.5 shrink-0" />
        {contact.tel}
      </a>
    )}
  </div>
);

/**
 * Firma muhatapları (#445, `contacts: {name, tel}[]`).
 *  - Yok → hiçbir şey çizilmez.
 *  - Tek muhatap → satır içinde ad + telefon.
 *  - Birden fazla → "Muhataplar (n)" düğmesi; liste sayfa katmanında (portal,
 *    fixed) açılır: tablo satırının yüksekliği değişmez, alttaki satırlar
 *    kaymaz, tablonun kaydırma alanında kesilmez. Dışarı tıklama, Escape ve
 *    sayfa kaydırma kapatır.
 */
export default function FirmContacts({ contacts }) {
  const list = Array.isArray(contacts) ? contacts.filter((c) => c && c.name) : [];
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const place = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8));
    const spaceBelow = window.innerHeight - rect.bottom;
    // Aşağıda yer yoksa düğmenin üstüne açılır
    setPos(
      spaceBelow < 200 && rect.top > spaceBelow
        ? { left, bottom: window.innerHeight - rect.top + 4 }
        : { left, top: rect.bottom + 4 }
    );
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (buttonRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    // Liste içinde kaydırma kapatmaz; sayfa / tablo kayınca kapanır
    const onScroll = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  if (list.length === 0) return null;

  if (list.length === 1) {
    return (
      <div className="flex items-start gap-1.5 min-w-0">
        <FaUserFriends className="text-gray-400 w-3.5 h-3.5 shrink-0 mt-0.5" title="Muhatap" />
        <ContactLine contact={list[0]} />
      </div>
    );
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        aria-haspopup="true"
        className={`relative z-10 inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-medium transition-colors ${
          open
            ? "bg-indigo-50 border-indigo-200 text-indigo-700"
            : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
        }`}
      >
        <FaUserFriends className="w-3.5 h-3.5 shrink-0 text-gray-400" />
        Muhataplar ({list.length})
        <FaChevronDown className={`w-2.5 h-2.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Muhataplar"
            style={{ position: "fixed", width: PANEL_WIDTH, ...pos }}
            className="z-[9999] max-h-64 overflow-y-auto bg-white rounded-xl border border-gray-200 shadow-lg p-1.5"
          >
            {list.map((contact, i) => (
              <div
                key={`${contact.name}-${i}`}
                className="px-2.5 py-2 rounded-lg hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
              >
                <ContactLine contact={contact} />
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}
